import type { AiCostLimitCreationFailedAction } from '@Application/AiCostLimits/Limits/Actions/aiCostLimitCreationFailed';
import type { AiCostLimitUpdateFailedAction } from '@Application/AiCostLimits/Limits/Actions/aiCostLimitUpdateFailed';
import CreateAiCostLimit from '@Application/AiCostLimits/Limits/Commands/CreateAiCostLimit';
import UpdateAiCostLimit from '@Application/AiCostLimits/Limits/Commands/UpdateAiCostLimit';
import createAiCostLimit from '@Application/AiCostLimits/Limits/createAiCostLimit';
import updateAiCostLimit from '@Application/AiCostLimits/Limits/updateAiCostLimit';
import type { AiCostLimitLevel } from '@Domain/AiCostLimits/AiCostLimitLevel';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import AiCostLimitFormData from '@UI/Pages/Administration/Shared/Sidebars/AiCostLimitSidebar/FormData/AiCostLimitFormData';
import SubjectField, {
	type SubjectOption,
} from '@UI/Pages/Administration/Shared/Sidebars/AiCostLimitSidebar/SubjectField';
import AiCostLimitUsersLoader from '@UI/Pages/Administration/Shared/AiCostLimitUsersLoader';
import resolveSubjectName from '@UI/Pages/Administration/Shared/Helpers/resolveSubjectName';
import aiCostLimitSidebarSignal, {
	closeAiCostLimitSidebar,
} from '@UI/Pages/Administration/Shared/Signals/aiCostLimitSidebarSignal';
import { css } from '@emotion/react';
import * as React from 'react';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { Button, Form, InputNumber, Sidebar } from '@verclam/ui-kit';

const helpTextStyle = css`
	margin: 0.75rem 1.25rem;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

type Props = {
	readonly accountId: number;
};

/**
 * One panel for creation (level fixed by the screen that opened it, subject chosen or preset) and
 * edition (level and subject are immutable). The structures and facilities must be loaded in the store
 * (`useAiCostLimitSubjects`); the screen mounts the panel once.
 */
const AiCostLimitSidebar: React.FC<Props> = ({ accountId }) => {
	const dispatch = useDispatch();
	const context = useSignalValue(aiCostLimitSidebarSignal);
	const limits = useSelector((state) => state.aiCostLimitsStore.limits);
	const users = useSelector((state) => state.aiCostLimitsStore.users);
	const subjects = useSelector((state) => state.aiCostLimitsStore.subjects);
	const formId = React.useId();

	const [formData, setFormData] = React.useState(() => AiCostLimitFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const editedLimit = context?.mode === 'edit' ? (limits?.findById(context.limitId) ?? null) : null;
	const level: AiCostLimitLevel | null =
		context?.mode === 'create' ? context.level : (editedLimit?.level ?? null);
	const presetSubjectId = context?.mode === 'create' ? context.subjectId : null;

	// Closes the panel with its screen: the signal is global and would reopen it on the next one.
	React.useEffect(() => closeAiCostLimitSidebar, []);

	React.useEffect(() => {
		if (context === null) {
			return;
		}

		if (editedLimit !== null) {
			setFormData(AiCostLimitFormData.fromView(editedLimit));
			return;
		}

		// An edition whose limit is gone falls back to an empty form (`presetSubjectId` is null then).
		setFormData(
			AiCostLimitFormData.createEmpty(
				context.mode === 'create' && context.level === 'account' ? accountId : presetSubjectId,
			),
		);
	}, [context, editedLimit, accountId, presetSubjectId]);

	// Subjects of the level that have no limit yet; the account has a single subject, itself.
	const subjectOptions = React.useMemo<readonly SubjectOption[]>(() => {
		if (level === null || level === 'account' || limits === null || subjects === null) {
			return [];
		}

		const limitedSubjectIds = new Set(limits.ofLevel(level).map((limit) => limit.subjectId));
		let candidates: readonly SubjectOption[] = [];

		if (level === 'organization') {
			candidates = subjects.organizations.map((organization) => ({
				id: organization.id,
				label: organization.name,
			}));
		} else if (level === 'facility') {
			candidates = subjects.facilities.map((facility) => ({ id: facility.id, label: facility.name }));
		} else {
			candidates = (users?.list ?? []).map((candidate) => ({
				id: candidate.id,
				label: candidate.fullName,
			}));
		}

		return candidates.filter((candidate) => !limitedSubjectIds.has(candidate.id));
	}, [level, limits, subjects, users]);

	const handleSubjectChange = React.useCallback((value: number | null) => {
		setFormData((current) => current.setSubjectId(value));
	}, []);

	const handleMonthlyAmountChange = React.useCallback((value: number | null) => {
		setFormData((current) => current.setMonthlyAmount(value));
	}, []);

	const handleAlertThresholdChange = React.useCallback((value: number | null) => {
		setFormData((current) => current.setAlertThresholdPercent(value));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		const commandData = formData.toCommandData();
		if (level === null || commandData === null) {
			return;
		}

		setIsSubmitting(true);

		if (editedLimit === null) {
			dispatch(createAiCostLimit(new CreateAiCostLimit({ accountId, level, ...commandData })))
				.then(() => closeAiCostLimitSidebar())
				.catch((action: AiCostLimitCreationFailedAction) =>
					updateValidationFromExternalSource({
						action,
						eventNames: 'AI_COST_LIMIT_CREATION_FAILED',
						formDataUpdater: setFormData,
					}),
				)
				.finally(() => setIsSubmitting(false));
			return;
		}

		dispatch(
			updateAiCostLimit(
				new UpdateAiCostLimit({
					accountId,
					limitId: editedLimit.id,
					monthlyAmount: commandData.monthlyAmount,
					alertThresholdPercent: commandData.alertThresholdPercent,
				}),
			),
		)
			.then(() => closeAiCostLimitSidebar())
			.catch((action: AiCostLimitUpdateFailedAction) =>
				updateValidationFromExternalSource({
					action,
					eventNames: 'AI_COST_LIMIT_UPDATE_FAILED',
					formDataUpdater: setFormData,
				}),
			)
			.finally(() => setIsSubmitting(false));
	}, [formData, accountId, level, editedLimit, dispatch]);

	const isEditMode = editedLimit !== null;
	const subjectLevel = level === null || level === 'account' ? null : level;
	const isSubjectReadOnly = isEditMode || presetSubjectId !== null;
	const hasNoSubjectLeft = !isSubjectReadOnly && subjectLevel !== null && subjectOptions.length === 0;
	const header = isEditMode ? 'Modifier le plafond' : 'Définir un plafond';

	return (
		<Sidebar
			visible={level !== null}
			onHide={closeAiCostLimitSidebar}
			position="right"
			header={header}
			footer={
				<React.Fragment>
					<Button
						type="button"
						severity="secondary"
						outlined
						label="Annuler"
						onClick={closeAiCostLimitSidebar}
					/>
					<Button
						type="submit"
						form={formId}
						label="Enregistrer"
						loading={isSubmitting}
						disabled={hasNoSubjectLeft}
					/>
				</React.Fragment>
			}
			blockScroll
		>
			<Form id={formId} onSubmit={handleSubmit} aria-label={header}>
				<ConditionalLoader data={subjectLevel}>
					{(chosenLevel) => {
						const subjectField = (
							<SubjectField
								level={chosenLevel}
								isReadOnly={isSubjectReadOnly}
								options={subjectOptions}
								value={formData.subjectId}
								subjectName={resolveSubjectName(
									chosenLevel,
									editedLimit?.subjectId ?? presetSubjectId ?? 0,
									subjects,
									users,
								)}
								errorMessage={formData.getValidationMessage('subjectId')}
								isValid={formData.isValid('subjectId')}
								onChange={handleSubjectChange}
							/>
						);

						return chosenLevel === 'user' ? (
							<AiCostLimitUsersLoader accountId={accountId}>
								{() => subjectField}
							</AiCostLimitUsersLoader>
						) : (
							subjectField
						);
					}}
				</ConditionalLoader>
				<InputNumber
					name="monthlyAmount"
					label="Plafond mensuel"
					required
					mode="currency"
					currency="EUR"
					maxFractionDigits={2}
					value={formData.monthlyAmount}
					onChange={handleMonthlyAmountChange}
					errorMessage={formData.getValidationMessage('monthlyAmount')}
					isFormFieldValid={formData.isValid('monthlyAmount')}
				/>
				<InputNumber
					name="alertThresholdPercent"
					label="Seuil d'alerte"
					required
					suffix=" %"
					value={formData.alertThresholdPercent}
					onChange={handleAlertThresholdChange}
					errorMessage={formData.getValidationMessage('alertThresholdPercent')}
					isFormFieldValid={formData.isValid('alertThresholdPercent')}
				/>
				<p css={helpTextStyle}>
					Au plafond, aucun nouveau traitement d'IA ne démarre. Un traitement déjà lancé va à son
					terme.
				</p>
			</Form>
		</Sidebar>
	);
};

export default AiCostLimitSidebar;
